/* eslint-disable @typescript-eslint/no-explicit-any */
import type { WritableDraft } from 'immer';

type SetFunction<T> = (updater: (state: WritableDraft<T>) => void, shouldReplace?: false, action?: string) => void;
type GetFunction<T> = () => T;

export type SliceCreator<T> = (set: any, get: any, store?: any) => Partial<T>;

function createNestedSliceHelpers<TParent, TKey extends keyof TParent>(
  set: SetFunction<TParent>,
  get: GetFunction<TParent>,
  nestedKey: TKey,
  store?: unknown,
) {
  const wrappedSet = <TChild = TParent[TKey]>(
    updater: (state: WritableDraft<TChild>) => void,
    shouldReplace?: false,
    action?: string,
  ) => {
    set(
      (state) => {
        updater(state[nestedKey] as WritableDraft<TChild>);
      },
      shouldReplace,
      action,
    );
  };

  const wrappedGet = () => get()[nestedKey];

  return { wrappedSet, wrappedGet, wrappedStore: store };
}

export function createNestedSlice<
  TParent,
  TKey extends keyof TParent,
  TSliceCreator extends SliceCreator<TParent[TKey]> = SliceCreator<TParent[TKey]>,
>(
  set: SetFunction<TParent>,
  get: GetFunction<TParent>,
  store: unknown,
  key: TKey,
  sliceCreators: ReadonlyArray<TSliceCreator>,
): TParent[TKey] {
  const { wrappedSet, wrappedGet, wrappedStore } = createNestedSliceHelpers(set, get, key, store);
  const parts = sliceCreators.map((sliceCreator) => sliceCreator(wrappedSet, wrappedGet, wrappedStore));

  return Object.assign({}, ...parts) as TParent[TKey];
}
